/**
 * Client-side calculation logic for the LaadCheck gratis calculator.
 *
 * Given user inputs it determines:
 *  - Recommended charging power (kW)
 *  - Estimated charging time per session
 *  - Estimated monthly costs (home vs public)
 */

import type { AutoModel } from './autoDatabase';

// Dutch average electricity tariff (home, 2024)
const TARIEF_THUIS_EUR_KWH = 0.3;
// Average public charging tariff (NL, 2024)
const TARIEF_PUBLIEK_EUR_KWH = 0.55;

// Weeks per month (avg)
const WEKEN_PER_MAAND = 4.33;

export type LaadMoment = 'nacht' | 'overdag';
export type LaadVermogen = 3.7 | 7.4 | 11 | 22;

export type AansluitingType = '1fase' | '3fase' | 'weet_niet';

export interface CalculatorInput {
  autoModel: AutoModel | null;   // null when orienting (no EV yet)
  kmPerDag: number;
  kmPiekDag: number;             // worst-case daily km (peak load estimate)
  dagenPerWeek: number;
  langeRitten: boolean;
  laadMoment: LaadMoment;
  beschikbareUren: number;       // available charging hours — replaces fixed urenPerMoment table
  laadtElders: 'nee' | 'soms' | 'regelmatig'; // reduces home charging requirement when charging elsewhere
  aantalEvs: 1 | 2 | 3;         // multiple EVs multiply required capacity
  zonnepanelen: boolean;
  aansluitingType: AansluitingType | null; // segmentation field — does not affect calculation
}

export type AdviesType = 'geschikt' | 'nader_onderzoek' | 'verzwaring_nodig';

export interface CalculatorResultaat {
  aanbevolenKw: LaadVermogen;
  adviesType: AdviesType;
  dagelijksVerbruikKwh: number;
  wekelijksVerbruikKwh: number;
  laadtijdUur: number;
  laadtijdMin: number;
  kmPerUurLaden: number;
  maandkostenThuis: number;
  maandkostenPubliek: number;
  besparing: number;
}

/**
 * Determine the recommended charging power, capped by the car's max AC.
 */
function bepaalAanbevolenKw(input: CalculatorInput): LaadVermogen {
  const maxCarKw = input.autoModel?.maxAcKw ?? 11;
  const kwhPer100Km = input.autoModel?.kwhPer100Km ?? 18.0;

  const beschikbareUren = input.beschikbareUren;

  const piekVerbruikKwh = (input.kmPiekDag * kwhPer100Km) / 100;
  const dagelijksVerbruikKwh = (input.kmPerDag * kwhPer100Km) / 100;

  // Weighted blend: 30% peak day + 70% average day
  const relevanteEnergie = piekVerbruikKwh * 0.3 + dagelijksVerbruikKwh * 0.7;
  let benodigdKw = relevanteEnergie / beschikbareUren;

  // Multiple EVs: capacity demand scales up
  benodigdKw *= input.aantalEvs;

  // Charging elsewhere: less needed at home
  if (input.laadtElders === 'regelmatig') benodigdKw *= 0.5;
  else if (input.laadtElders === 'soms') benodigdKw *= 0.8;

  // Long trips: car arrives near-empty unpredictably → 1.5× buffer
  if (input.langeRitten) benodigdKw *= 1.5;

  // Map to next standard charging tier; minimum 7.4 kW (3.7 kW too slow for real use)
  let aanbevolen: LaadVermogen;
  if (benodigdKw > 11) {
    aanbevolen = 22;
  } else if (benodigdKw > 4.5) {
    aanbevolen = 11;
  } else {
    aanbevolen = 7.4;
  }

  // Long trips: car may arrive near-empty regardless of daily average → floor at 11 kW
  if (input.langeRitten && aanbevolen < 11) aanbevolen = 11 as LaadVermogen;

  // Cap at what the car can accept
  const capped = Math.min(aanbevolen, maxCarKw);

  // Round to nearest valid option
  if (capped <= 3.7) return 3.7;
  if (capped <= 7.4) return 7.4;
  if (capped <= 11)  return 11;
  return 22;
}

export function berekenAdvies(input: CalculatorInput): CalculatorResultaat {
  const kwhPer100Km = input.autoModel?.kwhPer100Km ?? 18.0;

  // Daily energy consumption in kWh
  const dagelijksVerbruikKwh = (input.kmPerDag * kwhPer100Km) / 100;

  // Weekly energy consumption in kWh
  const wekelijksVerbruikKwh = dagelijksVerbruikKwh * input.dagenPerWeek;

  const aanbevolenKw = bepaalAanbevolenKw(input);

  // Derive advice type based on recommended power:
  //   22 kW → almost always requires a panel upgrade (verzwaring_nodig)
  //   11 kW → panel capacity unclear without full assessment (nader_onderzoek)
  //   3.7 / 7.4 kW → most panels handle this fine (geschikt)
  const adviesType: AdviesType =
    aanbevolenKw >= 22 ? 'verzwaring_nodig'
    : aanbevolenKw >= 11 ? 'nader_onderzoek'
    : 'geschikt';

  // Charging time per day (in hours)
  const laadtijdUurRaw = dagelijksVerbruikKwh / aanbevolenKw;
  const laadtijdUur = Math.floor(laadtijdUurRaw);
  const laadtijdMin = Math.round((laadtijdUurRaw - laadtijdUur) * 60);

  // km added per hour of charging
  const kmPerUurLaden = Math.round(aanbevolenKw / (kwhPer100Km / 100));

  // Monthly costs
  const maandelijksVerbruikKwh = wekelijksVerbruikKwh * WEKEN_PER_MAAND;
  const maandkostenThuis = Math.round(maandelijksVerbruikKwh * TARIEF_THUIS_EUR_KWH);
  const maandkostenPubliek = Math.round(maandelijksVerbruikKwh * TARIEF_PUBLIEK_EUR_KWH);
  const besparing = maandkostenPubliek - maandkostenThuis;

  return {
    aanbevolenKw,
    adviesType,
    dagelijksVerbruikKwh: Math.round(dagelijksVerbruikKwh * 10) / 10,
    wekelijksVerbruikKwh: Math.round(wekelijksVerbruikKwh * 10) / 10,
    laadtijdUur,
    laadtijdMin,
    kmPerUurLaden,
    maandkostenThuis,
    maandkostenPubliek,
    besparing,
  };
}
